import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, Typography } from '@mui/material';
import withObservables from '@nozbe/with-observables';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import Database from 'shared/db/services/Database.web';
import Entry from 'shared/db/services/Entry';
import Invoice from 'shared/db/services/Invoice';
import { EntryModel } from 'shared/types/Entries';

import {
    AddEditEntryModal,
    BackLinkButton,
    ErrorState,
    InvoiceLimitReachedModal,
} from '@/components';
import AddEditInvoiceModal from '@/components/AddEditInvoiceModal';
import { useImagesContext } from '@/context/ImagesContext';
import { useInvoicingSystemContext } from '@/context/InvoicingSystemContext';
import { useDatabase, useMonthlyInvoicesLimit } from '@/hooks';
import { ROUTE } from '@/router/routes';
import { databaseValidation } from '@/services/databaseValidation';
import { FirebaseImagesService } from '@/services/firebase/images';
import { dbServiceLogger } from '@/services/logger/logger';

import { EntryDetails } from './components';
import { Props } from './types';

function EntryPage({ monthlyInvoicesCount = 0 }: Props) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { getDatabase } = useDatabase();
    const { ImagesService } = useImagesContext();
    const { id } = useParams();

    const [hasError, setHasError] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [entry, setEntry] = useState<EntryModel | null>(null);
    const [isAddInvoiceModalOpen, setIsAddInvoiceModalOpen] =
        useState<boolean>(false);
    const [showInvoiceLimitReachedModal, setShowInvoiceLimitReachedModal] =
        useState(false);

    const { isInvoicingAvailable } = useInvoicingSystemContext();
    const { isInvoiceLimitReached } =
        useMonthlyInvoicesLimit(monthlyInvoicesCount);

    const database = useMemo(() => getDatabase(), [getDatabase]);

    const openEditModal = useCallback(() => setIsEditModalOpen(true), []);
    const closeEditModal = useCallback(() => setIsEditModalOpen(false), []);

    const openAddInvoiceModal = useCallback(
        () => setIsAddInvoiceModalOpen(true),
        [],
    );
    const closeAddInvoiceModal = useCallback(
        () => setIsAddInvoiceModalOpen(false),
        [],
    );

    const handleEntryRemoved = useCallback(
        () => navigate(ROUTE.entries),
        [navigate],
    );

    const navigateToInvoicesFallback = useCallback(() => {
        navigate(ROUTE.invoices);
    }, [navigate]);

    const handleAddNewInvoiceClick = useCallback(() => {
        if (isInvoiceLimitReached) {
            setShowInvoiceLimitReachedModal(true);
            return;
        }

        isInvoicingAvailable
            ? openAddInvoiceModal()
            : navigateToInvoicesFallback();
    }, [
        isInvoicingAvailable,
        openAddInvoiceModal,
        navigateToInvoicesFallback,
        isInvoiceLimitReached,
    ]);

    const closeInvoiceLimitReachedModal = useCallback(() => {
        setShowInvoiceLimitReachedModal(false);
    }, []);

    const fetchEntry = useCallback(async () => {
        setIsLoading(true);
        setHasError(false);

        try {
            const entryService = new Entry({
                database,
                imageService: ImagesService,
                logger: dbServiceLogger,
                validation: databaseValidation,
            });

            if (!id) {
                throw new Error();
            }

            const entry = await entryService.getByID(id);

            setEntry(entry);
        } catch {
            setHasError(true);
        } finally {
            setIsLoading(false);
        }
    }, [ImagesService, database, id]);

    useEffect(() => {
        fetchEntry();
    }, [fetchEntry]);

    return (
        <Box sx={{ pb: 10, minWidth: 400 }}>
            {isEditModalOpen && entry ? (
                <AddEditEntryModal
                    close={closeEditModal}
                    onRemove={handleEntryRemoved}
                    entry={entry}
                    isEditMode
                />
            ) : null}
            <Box
                sx={{
                    mb: 3.25,
                    display: 'flex',
                    justifyContent: 'space-between',
                }}
            >
                <BackLinkButton
                    text={t('Entry:button:back')}
                    to={ROUTE.entries}
                    testID="EntryPage-BackButton"
                />
                {!hasError && entry ? (
                    <Button
                        data-test-id="EntryPage-EditButton"
                        sx={{ display: 'flex', gap: 1.5 }}
                        color="inherit"
                        disabled={isLoading}
                        onClick={openEditModal}
                        size="small"
                    >
                        <EditIcon />
                        <Typography fontSize="small" fontWeight={700}>
                            {t('Entry:button:edit')}
                        </Typography>
                    </Button>
                ) : null}
            </Box>
            {hasError ? (
                <ErrorState
                    onRefreshClick={fetchEntry}
                    testID="EntryPage-ErrorState"
                />
            ) : (
                <EntryDetails
                    entry={entry}
                    isLoading={isLoading}
                    onCreateInvoice={handleAddNewInvoiceClick}
                />
            )}
            {isAddInvoiceModalOpen && entry ? (
                <AddEditInvoiceModal
                    close={closeAddInvoiceModal}
                    entry={entry}
                    closeOnSubmit
                />
            ) : null}
            {showInvoiceLimitReachedModal && (
                <InvoiceLimitReachedModal
                    onClose={closeInvoiceLimitReachedModal}
                />
            )}
        </Box>
    );
}

const enhance = withObservables<Props, unknown>([], () => {
    const database = Database.getDatabase();
    const invoiceService = new Invoice({
        database,
        imageService: new FirebaseImagesService(),
        logger: dbServiceLogger,
        validation: databaseValidation,
    });
    const monthlyInvoicesCount = invoiceService.observeCreatedThisMonth();

    return {
        monthlyInvoicesCount,
    };
});

export default enhance(EntryPage);
